import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useFinca } from '../context/FincaContext';

export default function Pagar() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const { fincaActiva } = useFinca();

  const [config, setConfig] = useState(null);
  const [miPlan, setMiPlan] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  // Formulario
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [metodo, setMetodo] = useState('transferencia');
  const [notas, setNotas] = useState('');
  const [comprobante, setComprobante] = useState(null); // base64
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [cfgRes, planRes] = await Promise.all([
        api.get('/config-pagos'),
        api.get('/suscripciones/mi-plan'),
      ]);
      setConfig(cfgRes.data || null);
      setMiPlan(planRes.data || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  // Prefill monto con precio del plan actual
  useEffect(() => {
    if (miPlan && !monto) {
      const precio = miPlan.ciclo === 'anual'
        ? Number(miPlan.precio_anual)
        : Number(miPlan.precio_mensual);
      if (precio > 0) setMonto(String(precio));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miPlan]);

  const handleArchivo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('El archivo es muy grande. Máximo 2 MB.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setComprobante(reader.result); // data:image/...;base64,XXX
      setNombreArchivo(file.name);
    };
    reader.readAsDataURL(file);
  };

  const enviar = async () => {
    if (!monto || Number(monto) <= 0) {
      alert('Ingresa el monto pagado');
      return;
    }
    if (!comprobante) {
      alert('Sube el comprobante de pago');
      return;
    }

    setEnviando(true);
    setMensaje(null);
    try {
      const { data } = await api.post('/pagos/solicitar', {
        monto: Number(monto),
        metodo,
        referencia: referencia.trim() || null,
        notas: notas.trim() || null,
        comprobante_base64: comprobante,
      });
      setMensaje({
        tipo: 'ok',
        texto: data.mensaje || 'Comprobante enviado. Espera verificación.',
      });
      setMonto('');
      setReferencia('');
      setNotas('');
      setComprobante(null);
      setNombreArchivo('');
      setTimeout(cargar, 800);
    } catch (e) {
      setMensaje({
        tipo: 'error',
        texto: e.response?.data?.error || 'Error al enviar el comprobante',
      });
    } finally {
      setEnviando(false);
    }
  };

  const diasRestantes = miPlan?.dias_restantes;
  const vencida = diasRestantes != null && diasRestantes <= 0;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-700">Renovar mi plan</h1>
          <p className="text-sm text-gray-500 mt-1">
            {fincaActiva?.nombre || 'Mi finca'} · {miPlan ? `Plan ${miPlan.plan_nombre}` : 'Sin plan'}
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 border border-slate-400 rounded-lg hover:bg-slate-100 text-sm"
        >
          ← Volver
        </button>
      </div>

      {/* Estado del plan */}
      {miPlan && (
        <div className={`rounded-xl p-4 mb-6 border-2 ${vencida ? 'bg-red-50 border-red-300' : diasRestantes <= 7 ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-300'}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">
                {vencida ? '🔴 Licencia vencida' : diasRestantes <= 7 ? '⚠️ Por vencer' : '✅ Activa'}
              </div>
              <div className="text-xl font-bold mt-1">
                {miPlan.plan_nombre} · {format(new Date(miPlan.fecha_vencimiento), 'dd/MM/yyyy')}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {vencida
                  ? `Venció hace ${Math.abs(diasRestantes)} día(s)`
                  : `${diasRestantes} día(s) restantes`}
              </div>
            </div>
          </div>
        </div>
      )}

      {cargando ? (
        <div className="p-12 text-center text-gray-500">Cargando...</div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg">{error}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ─── Métodos de pago ─── */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-700">
              💳 Formas de pago
            </h2>

            {/* QR DeUna / Pichincha */}
            {config?.qr_base64 && (
              <div className="bg-white rounded-xl shadow-sm p-5 border">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">📱</span>
                  <div>
                    <div className="font-semibold text-slate-800">QR DeUna / Pichincha</div>
                    <div className="text-xs text-gray-500">Escanea con la app del banco</div>
                  </div>
                </div>
                <div className="flex justify-center bg-slate-50 rounded-lg p-4">
                  <img
                    src={config.qr_base64}
                    alt="QR DeUna"
                    style={{ maxWidth: 260, width: '100%' }}
                  />
                </div>
              </div>
            )}

            {/* Transferencia bancaria */}
            {(config?.cuenta_pichincha || config?.titular) && (
              <div className="bg-white rounded-xl shadow-sm p-5 border">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">🏦</span>
                  <div>
                    <div className="font-semibold text-slate-800">
                      {config.banco || 'Banco Pichincha'}
                    </div>
                    <div className="text-xs text-gray-500">Transferencia / Depósito</div>
                  </div>
                </div>
                <dl className="space-y-2 text-sm">
                  {config.tipo_cuenta && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Tipo</dt>
                      <dd className="font-medium">{config.tipo_cuenta}</dd>
                    </div>
                  )}
                  {config.cuenta_pichincha && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Nº Cuenta</dt>
                      <dd className="font-mono font-bold text-slate-800 select-all">
                        {config.cuenta_pichincha}
                      </dd>
                    </div>
                  )}
                  {config.titular && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Titular</dt>
                      <dd className="font-medium text-right">{config.titular}</dd>
                    </div>
                  )}
                  {config.identificacion && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">CI/RUC</dt>
                      <dd className="font-mono">{config.identificacion}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* PayPal */}
            {config?.paypal_link && (
              <div className="bg-white rounded-xl shadow-sm p-5 border">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">🌎</span>
                  <div>
                    <div className="font-semibold text-slate-800">PayPal</div>
                    <div className="text-xs text-gray-500">Para pagos internacionales</div>
                  </div>
                </div>
                <a
                  href={config.paypal_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                >
                  Pagar con PayPal →
                </a>
              </div>
            )}

            {config?.instrucciones && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 whitespace-pre-wrap">
                {config.instrucciones}
              </div>
            )}

            {!config?.qr_base64 && !config?.cuenta_pichincha && !config?.paypal_link && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-500 text-sm">
                ⚠️ El administrador aún no ha configurado los métodos de pago.<br />
                Contacta al área de ventas: <strong>+593 98 156 0147</strong>
              </div>
            )}
          </div>

          {/* ─── Subir comprobante ─── */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-700">
              📎 Subir comprobante
            </h2>

            <div className="bg-white rounded-xl shadow-sm p-5 border space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Monto pagado (USD) *</span>
                <input
                  type="number" step="0.01" min="0"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  placeholder="Ej: 80.00"
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Método *</span>
                <select
                  value={metodo}
                  onChange={(e) => setMetodo(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="transferencia">Transferencia bancaria</option>
                  <option value="payphone">QR DeUna / Payphone</option>
                  <option value="paypal">PayPal</option>
                  <option value="otro">Otro</option>
                </select>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Referencia / Nº comprobante</span>
                <input
                  type="text"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  placeholder="Ej: 987654321"
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-mono"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Comprobante (imagen o PDF) *</span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleArchivo}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700"
                />
                {nombreArchivo && (
                  <div className="text-[11px] text-green-700 mt-1">
                    ✅ {nombreArchivo}
                  </div>
                )}
                <span className="text-[11px] text-gray-500">Máximo 2 MB</span>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Notas adicionales</span>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  rows={2}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="Ej: pago del mes de octubre"
                />
              </label>

              {mensaje && (
                <div className={`p-3 rounded-lg text-sm ${mensaje.tipo === 'ok' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {mensaje.texto}
                </div>
              )}

              <button
                onClick={enviar}
                disabled={enviando}
                className="w-full py-3 bg-slate-700 text-white rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50"
              >
                {enviando ? 'Enviando...' : '📤 Enviar comprobante'}
              </button>

              <p className="text-[11px] text-gray-500 text-center">
                Un administrador revisará tu pago en las próximas horas y activará tu plan.
              </p>
            </div>

            {/* Ayuda directa */}
            <div className="bg-slate-50 border rounded-lg p-4 text-center text-xs text-gray-600">
              ¿Prefieres contactarnos? <br />
              <strong>WhatsApp:</strong> +593 98 156 0147 · <strong>Email:</strong> Ventas@lluchunga.com
            </div>
          </div>
        </div>
      )}
    </div>
  );
}